import React, { FC, useEffect, useRef, useState } from 'react';
import { CoachApi } from '../../models/Coach';
import { ApiProfile } from '../../api/auth';
import Pagination from '../../kit/collections/Table/Pagination';
import { ApiMeta } from '../../api';
import Loader from '../../components/Loader';
import { Checkbox, Form, Table } from '../../kit';
import styles from './styles.module.scss';
import { useDropdownOptions } from '../../lib/hooks';

export const regions = [
    'Brabant',
    'Drenthe',
    'Flevoland',
    'Friesland',
    'Gelderland',
    'Groningen',
    'Limburg',
    'Noord-Holland',
    'Overijssel',
    'Utrecht',
    'Zeeland',
    'Zuid-Holland',
    'Heel Nederland',
];

type FilterType = 'workarea' | 'expertise';

interface SearchFilter {
    expertise: string[],
    workarea: string[],
};

const ProfileView: FC = (): JSX.Element => {
    const [ coaches, setCoaches ] = useState<ApiProfile[]>([]);
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ meta, setMeta ] = useState<ApiMeta>();
    const offline = useRef<boolean>(false);
    const query = useRef<string>('');
    const searchFilter = useRef<SearchFilter>({
        expertise: [],
        workarea: [],
    });

    const expertiseOptions = useDropdownOptions('labels?type=expertise');

    useEffect(() => {
        if (coaches.length <= 0) return;
        (window as any).AOS.init();
    }, [coaches]);

    const fetch = (page: number = 1) => {
        setIsLoading(true);
        CoachApi.list({
            offline: offline.current ? '1' : '0',
            page,
            query: query.current || '',
            workarea: searchFilter.current.workarea.join(','),
            expertise: searchFilter.current.expertise.join(',')
        }).then(({ data }) => {
            if (page === 1) {
                setCoaches(data.data);
            } else {
                setCoaches((c) => [ ...c, ...data.data ]);
            }
            setMeta(data.meta);
            setIsLoading(false);
        });
    }
    useEffect(() => fetch(), []);

    const handleFilter = (type: FilterType, value: string): void => {
        searchFilter.current = {
            ...searchFilter.current,
            [type]: [
                ...searchFilter.current[type],
                value,
            ],
        }
        fetch(1);
    }

    const removeFilter = (type: FilterType, index: number): void => {
        searchFilter.current = {
            ...searchFilter.current,
            [type]: searchFilter.current[type].filter((o, i) => i !== index),
        }
        fetch(1);
    }

    return (<>
        <h2 className="section-title" data-aos="fade-up">Profielen</h2>
        <Table.Actions
            onSearch={(newQuery) => {
                query.current = newQuery;
                window.scrollTo({ top: 0, behavior: 'smooth' });
                fetch(1);
            }}
            extra={(
                <Checkbox
                    checked={offline.current}
                    label="Offline academy afgerond"
                    onChange={() => {
                        offline.current = !offline.current
                        fetch();
                    }}
                    toggle
                />
            )}
        >
            <div className={styles.filter}>
                <div>
                    <Form.Dropdown
                        onChange={({ value }) => handleFilter('expertise', value)}
                        options={expertiseOptions.map((o) => ({
                            text: o.text,
                            value: o.text,
                        }))}
                        placeholder="Zoeken op expertise"
                    />
                </div>
                <div>
                    <Form.Dropdown
                        onChange={({ value }) => handleFilter('workarea', value)}
                        options={regions.map((o) => ({
                            text: o,
                            value: o,
                        }))}
                        placeholder="Zoeken op werkgebied(en)"
                    />
                </div>
                <div className={styles.tags}>
                    {['workarea', 'expertise'].map((t: any) => {
                        return (searchFilter.current as any)[t].map((tag: string, index: number) => (
                            <div
                                key={`tag-${t}-${index}`}
                                onClick={() => removeFilter(t, index)}
                            >
                                {tag}
                                <i className="far fa-times" />
                            </div>
                        ));
                    })}
                </div>
            </div>
        </Table.Actions>
        <div className="container">
            <div className="people small-people">
                {coaches.length > 0 ? coaches.map((coach, index) => (
                    <div key={`coach-${coach.id}`} onClick={() => window.location.href = `/community/${coach.permalink}`} style={{ cursor: 'pointer' }}>
                        <div className="person" data-aos="fade-up">
                            <div
                                className="person__avatar person__avatar--square"
                                style={{ backgroundImage: `url(${coach.avatar || `${process.env.REACT_APP_WP_URL}assets/images/placeholder.jpg`}` }}
                            >
                                <div className="person__trophies">
                                    {(coach.academy_offline || coach.academy_online) && (
                                        <span className="grad"><i className="far fa-graduation-cap" /></span>
                                    )}
                                    {coach.subscription === 'full' && (
                                        <span><i className="far fa-star" /></span>
                                    )}
                                </div>
                            </div>
                            <div className="person__meta">
                                <h3>{coach.name}</h3>
                                <p>{coach.organisation} - {coach.function}</p>
                            </div>
                            <a href={`/community/${coach.permalink}`}>
                                Bekijk profiel
                                <i className="fa-regular fa-arrow-right"></i>
                            </a>
                        </div>
                    </div>
                )) : (
                    <em>Geen resultaten</em>
                )}
                {isLoading && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Loader />
                    </div>
                )}
            </div>
            {meta && (
                <Pagination
                    meta={meta}
                    onChange={(newPage) => fetch(newPage)}
                    isLoading={isLoading}
                    infinite
                />
            )}
        </div>
    </>);
}

export default ProfileView;
